'use client';

import { useState, type FormEvent } from 'react';
import { Icon } from './icon';

/**
 * Phase 32.6 · spec §8.3.9 / §6.7 — public agent contact form.
 *
 * The root element carries `id="inquiry-form"` because the sticky `ContactRail`
 * "Contact agent" CTA anchors to `#inquiry-form`. On submit it POSTs JSON to
 * `/api/agents/{slug}/inquiry` (org resolved server-side from the `x-org-slug`
 * header); the route persists the row first, then emails the agent, so a visitor
 * submission is never lost even if the email send fails.
 *
 * Client component — owns the controlled fields + submitting/success/error
 * state. Native validation (`required`, `type="email"`) gates the obvious cases;
 * the route Zod-validates authoritatively. On success we show a thank-you panel
 * and reset the fields; on error a tokenized inline message. Submit is disabled
 * while a request is in flight.
 *
 * Theme tokens only — layout via Tailwind, all color/shadow via `--brand-*`
 * inline styles (the `agent-card.tsx` idiom).
 */

const INQUIRER_TYPES = [
  { value: 'seller', label: 'Seller' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'renter', label: 'Renter' },
] as const;

type InquirerType = (typeof INQUIRER_TYPES)[number]['value'];

const inputStyle: React.CSSProperties = {
  backgroundColor: 'var(--brand-input-bg, var(--brand-card-bg))',
  borderColor: 'var(--brand-input-border, var(--brand-card-border))',
  color: 'var(--brand-text)',
  borderRadius: 'var(--radius-input, 6px)',
  fontSize: '14px',
};

const labelStyle: React.CSSProperties = {
  color: 'var(--brand-text)',
  fontSize: '13px',
  fontWeight: 600,
};

export function InquiryForm({
  slug,
  heading = 'Contact this agent',
  embedded = false,
}: {
  slug: string;
  /** Visible heading + aria-label (e.g. "Contact Mindy" on the property page). */
  heading?: string;
  /** The profile dialog supplies its own heading and card shell. */
  embedded?: boolean;
}) {
  const [inquirerType, setInquirerType] = useState<InquirerType>('seller');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`/api/agents/${slug}/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inquirerType, name, email, phone, message }),
      });

      if (!res.ok) {
        throw new Error('Request failed');
      }

      // Success — show the thank-you panel and clear the fields.
      setSubmitted(true);
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
      setInquirerType('seller');
    } catch {
      setError(
        "Something went wrong sending your message. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <section
        id="inquiry-form"
        aria-label={heading}
        className={embedded ? "" : "rounded-lg border p-6"}
        style={{
          borderColor: 'var(--brand-card-border)',
          borderRadius: 'var(--radius-card, 8px)',
          boxShadow: embedded ? undefined : 'var(--shadow-resting)',
          backgroundColor: 'var(--brand-card-bg)',
        }}
      >
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <span
            className="flex h-12 w-12 items-center justify-center rounded-full"
            style={{ backgroundColor: 'var(--brand-surface-subtle)' }}
          >
            <Icon
              name="check"
              size={28}
              style={{ color: 'var(--brand-primary)' }}
            />
          </span>
          <h2
            className="font-semibold"
            style={{ color: 'var(--brand-text)', fontSize: '18px' }}
          >
            Thanks &mdash; your message has been received
          </h2>
          <p
            style={{ color: 'var(--brand-text-muted)', fontSize: '14px' }}
          >
            Your inquiry has been saved for the agent.
          </p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium"
            style={{ color: 'var(--brand-primary)' }}
          >
            Send another message
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      id="inquiry-form"
      aria-label={heading}
      className={embedded ? "" : "rounded-lg border p-6"}
      style={{
        borderColor: 'var(--brand-card-border)',
        borderRadius: 'var(--radius-card, 8px)',
        boxShadow: embedded ? undefined : 'var(--shadow-resting)',
        backgroundColor: 'var(--brand-card-bg)',
      }}
    >
      {!embedded && <h2
        className="flex items-center gap-2 font-semibold"
        style={{
          color: 'var(--brand-text)',
          fontSize: '18px',
          letterSpacing: '-0.01em',
        }}
      >
        <Icon name="mail" size={18} style={{ color: 'var(--brand-primary)' }} />
        {heading}
      </h2>}

      <form className={`${embedded ? "" : "mt-4 "}flex flex-col gap-4`} onSubmit={handleSubmit} noValidate={false}>
        {/* I am a — Seller / Buyer / Renter */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inquiry-inquirer-type" style={labelStyle}>
            I am a
          </label>
          <select
            id="inquiry-inquirer-type"
            name="inquirerType"
            value={inquirerType}
            onChange={(e) => setInquirerType(e.target.value as InquirerType)}
            required
            className="w-full border px-3 py-2"
            style={inputStyle}
          >
            {INQUIRER_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Your Name */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inquiry-name" style={labelStyle}>
            Your Name
          </label>
          <input
            id="inquiry-name"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
            className="w-full border px-3 py-2"
            style={inputStyle}
          />
        </div>

        {/* Email */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inquiry-email" style={labelStyle}>
            Email
          </label>
          <input
            id="inquiry-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="w-full border px-3 py-2"
            style={inputStyle}
          />
        </div>

        {/* Phone (optional) */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inquiry-phone" style={labelStyle}>
            Phone
          </label>
          <input
            id="inquiry-phone"
            name="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            className="w-full border px-3 py-2"
            style={inputStyle}
          />
        </div>

        {/* Message */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inquiry-message" style={labelStyle}>
            Message
          </label>
          <textarea
            id="inquiry-message"
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={4}
            className="w-full border px-3 py-2"
            style={inputStyle}
          />
        </div>

        {error && (
          <p
            role="alert"
            className="flex items-center gap-1.5"
            style={{ color: 'var(--brand-accent)', fontSize: '13px' }}
          >
            <Icon name="close" size={16} />
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 font-semibold"
          style={{
            backgroundColor: 'var(--brand-primary)',
            color: 'var(--brand-primary-foreground)',
            borderRadius: 'var(--radius-button, 6px)',
            fontSize: '15px',
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          <Icon name="send" size={18} />
          {submitting ? 'Sending…' : 'Send message'}
        </button>
      </form>
    </section>
  );
}
